
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AlertCircle, Ban } from "lucide-react";
import { Slab } from "@/types/slab";
import { formatCurrency, getFullImageUrl, stockStatusStyles } from "@/lib/utils";

interface SlabCardProps {
  slab: Slab;
  onClick: (slab: Slab) => void;
}

const typeColors: Record<string, string> = {
  Marble: "bg-[#FFDEE280]", // Soft Pink
  Granite: "bg-[#F2FCE280]", // Soft Green
  Quartzite: "bg-[#D3E4FD80]", // Soft Blue
  Quartz: "bg-[#FDE1D380]", // Soft Peach
  Calcite: "bg-[#E5DEFF80]", // Soft Purple
  Other: "bg-[#F1F0FB80]", // Soft Gray
};

export function SlabCard({ slab, onClick }: SlabCardProps) {
  const isLowStock = slab.quantity < 2 && slab.quantity > 0;
  const isOutOfStock = slab.quantity === 0;
  const typeColor = typeColors[slab.type] || typeColors.Other;

  return (
    <Card 
      className={`group hover:shadow-lg transition-shadow duration-200 cursor-pointer ${
        isOutOfStock ? 'opacity-75' : ''
      }`}
      onClick={() => onClick(slab)}
    >
      <CardContent className="p-0 relative">
        <div className="aspect-[4/3] relative overflow-hidden rounded-t-lg">
          <img
            src={getFullImageUrl(slab.imageUrl)}
            alt={slab.name}
            className={`object-cover w-full h-full group-hover:scale-105 transition-transform duration-200 ${
              isOutOfStock ? 'grayscale' : ''
            }`}
            onError={(e) => {
              e.currentTarget.src = '/placeholder.svg';
            }}
          />
          {isOutOfStock ? (
            <div className="absolute inset-0 flex items-center justify-center bg-black/5 backdrop-blur-[1px]">
              <Badge variant="destructive" className="gap-1">
                <Ban className="h-4 w-4" />
                Out of Stock
              </Badge>
            </div>
          ) : isLowStock ? (
            <div className="absolute top-2 right-2">
              <Badge variant="secondary" className={`gap-1 ${stockStatusStyles.lowStock.badge}`}>
                <AlertCircle className="h-4 w-4" />
                Low Stock
              </Badge>
            </div>
          ) : null}
        </div>
      </CardContent>
      <CardFooter 
        className={`flex flex-col items-start gap-2 p-4 ${
          isOutOfStock ? 'grayscale' : ''
        }`}
      >
        <div className="flex justify-between w-full">
          <span className="font-semibold">{slab.code}</span>
          <Badge variant="outline" className={`${typeColor} border-none text-gray-700`}>
            {slab.type}
          </Badge>
        </div>
        <h3 className="font-medium text-lg">{slab.name}</h3>
        <div className="flex justify-between w-full text-sm text-muted-foreground">
          <span>{formatCurrency(slab.price)}</span>
          <span>{slab.quantity} available{slab.quantity !== 1 ? 's' : ''}</span>
        </div>
      </CardFooter>
    </Card>
  );
}
